import React, { useState, useEffect } from 'react'
import { useParams, useHistory, useLocation } from 'react-router-dom'

import PropTypes from 'prop-types'

import { LoadingFeedback } from 'components'
import ActivityModal from '../ActivityModal'

import helpers from 'helpers'

import useSnackbar from 'hooks/useSnackbar'

import { routes } from 'Routes'
import * as service from 'service'

const ActivityForm = ({ isShow = false }) => {
  const { id } = useParams()
  const history = useHistory()
  const location = useLocation()
  const snackbar = useSnackbar()

  const locationState = location.state || {}

  const [isSaving, setIsSaving] = useState(false)
  const [users, setUsers] = useState(locationState.users || [])
  const [isLoading, setIsLoading] = useState(!locationState.task)
  const [columns, setColumns] = useState(locationState.columns || [])
  const [activity, setActivity] = useState(locationState.task || null)
  const [departments, setDepartments] = useState(
    locationState.departments || [],
  )
  const [initialComments, setInitialComments] = useState(
    locationState.comments ?? null,
  )

  useEffect(() => {
    if (locationState.task) return
    const load = async () => {
      setIsLoading(true)
      try {
        const requests = [
          service.dponet.kanbanTasks.get({
            taskId: id,
            includeComments: true,
          }),
          service.dponet.kanbanBoards.get(),
          ...(!isShow
            ? [
                service.dponet.users.get({ minimal: true, perPage: 9999 }),
                service.dponet.departments.get({ perPage: 9999 }),
              ]
            : []),
        ]

        const [actRes, boardRes, usersRes, deptsRes] = await Promise.all(
          requests,
        )

        const task = actRes?.data?.task
        setActivity(task)
        setInitialComments(task?.taskComments || [])

        const boards = boardRes?.data?.kanbanBoards || []
        const boardId =
          task?.kanbanBoardId ||
          (boards.find((b) => b.isDefault) || boards[0])?.id
        if (boardId) {
          const colsRes = await service.dponet.kanbanColumns.get({
            kanbanBoardId: boardId,
          })
          setColumns(colsRes?.data?.kanbanColumns || [])
        }

        if (!isShow) {
          setUsers(usersRes?.data?.users || [])
          setDepartments(deptsRes?.data?.departments || [])
        }
      } catch (error) {
        snackbar.open({
          message: helpers.formatters.errorMessage(error),
          variant: 'error',
        })
        history.push(routes.activitiesPanel.all)
      } finally {
        setIsLoading(false)
      }
    }
    load()
    //eslint-disable-next-line
  }, [id])

  const handleSave = async (data, taskId) => {
    setIsSaving(true)
    try {
      await service.dponet.kanbanTasks.update({ taskId, ...data })
      if (data.isFinalize) {
        await service.dponet.kanbanTasks.finalize({ taskId })
      } else if (data.isMoveColumn) {
        await service.dponet.kanbanTasks.move({
          taskId,
          kanbanColumnId: data.kanbanColumnId,
        })
      }
      if (data.attachments && taskId) {
        await service.dponet.kanbanTasks.uploadAttachments({
          taskId,
          attachments: data.attachments,
        })
      }
      snackbar.open({
        message: 'Atividade salva com sucesso',
        variant: 'success',
      })
    } catch (error) {
      snackbar.open({
        message: helpers.formatters.errorMessage(error),
        variant: 'error',
      })
      throw error
    } finally {
      setIsSaving(false)
    }
  }

  const handleRemoveAttachment = async (taskId, attachmentId) => {
    try {
      await service.dponet.kanbanTasks.destroyAttachment({
        taskId,
        attachmentId,
      })
    } catch (error) {
      snackbar.open({
        message: helpers.formatters.errorMessage(error),
        variant: 'error',
      })
    }
  }

  const handleClose = (value) => {
    if (!value) history.push(routes.activitiesPanel.all)
  }

  return (
    <>
      <LoadingFeedback open={isLoading} />
      {activity && (
        <ActivityModal
          open={true}
          setOpen={handleClose}
          activity={activity}
          columns={columns}
          users={users}
          departments={departments}
          isShow={isShow}
          fullPage
          initialComments={initialComments}
          {...(!isShow && {
            onSave: handleSave,
            onRemoveAttachment: handleRemoveAttachment,
            isSaving,
          })}
        />
      )}
    </>
  )
}

ActivityForm.propTypes = {
  isShow: PropTypes.bool,
}

export default ActivityForm
